import { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  value: number;
  onChange?: (rating: number) => void;
  readonly?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizes = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8",
};

export function StarRating({
  value,
  onChange,
  readonly = false,
  size = "md",
  className,
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState(0);

  const displayValue = hoverValue || value;

  return (
    <div className={cn("flex gap-1", className)} role="group" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readonly && setHoverValue(star)}
          onMouseLeave={() => !readonly && setHoverValue(0)}
          className={cn(
            "transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] rounded",
            !readonly && "hover:scale-110 cursor-pointer",
            readonly && "cursor-default"
          )}
          aria-label={`${star} star${star === 1 ? "" : "s"}`}
          aria-pressed={value === star}
        >
          <Star
            className={cn(
              sizes[size],
              "transition-colors",
              displayValue >= star
                ? "fill-amber-400 text-amber-400"
                : "fill-transparent text-[var(--color-border)]"
            )}
          />
        </button>
      ))}
    </div>
  );
}
